/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Hook for bulk selection management
 */

"use client";

import { logger } from "@/lib/utils/logger";
import { useCallback, useState } from "react";

interface UseBulkSelectionOptions<T> {
  items: T[];
  keyExtractor: (item: T) => string;
  onSelectionChange?: (ids: Set<string>, items: T[]) => void;
}

/**
 * Hook for managing bulk selection state
 */
export function useBulkSelection<T>({
  items,
  keyExtractor,
  onSelectionChange,
}: UseBulkSelectionOptions<T>) {
  // Selection state using Set for O(1) lookups
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Get selected items
  const selectedItems = items.filter((item) =>
    selectedIds.has(keyExtractor(item)),
  );

  // Select all items
  const selectAll = useCallback(() => {
    const allIds = new Set(items.map(keyExtractor));
    setSelectedIds(allIds);
    onSelectionChange?.(allIds, items);
  }, [items, keyExtractor, onSelectionChange]);

  // Clear all selections
  const clearSelection = useCallback(() => {
    logger.debug("clearSelection called - clearing selection");
    setSelectedIds(new Set());
    onSelectionChange?.(new Set(), []);
    logger.debug("clearSelection completed");
  }, [onSelectionChange]);

  // Toggle single item selection
  const toggleItem = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        const selected = items.filter((item) => next.has(keyExtractor(item)));
        onSelectionChange?.(next, selected);
        return next;
      });
    },
    [items, keyExtractor, onSelectionChange],
  );

  // Set selection to specific IDs
  const setSelection = useCallback(
    (ids: Set<string>) => {
      setSelectedIds(ids);
      const selected = items.filter((item) => ids.has(keyExtractor(item)));
      onSelectionChange?.(ids, selected);
    },
    [items, keyExtractor, onSelectionChange],
  );

  return {
    selectedIds,
    setSelectedIds,
    selectedItems,
    selectedCount: selectedIds.size,
    selectAll,
    clearSelection,
    toggleItem,
    setSelection,
  };
}
